import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiGithub as Github,
  FiTwitter as Twitter,
  FiYoutube as Youtube,
  FiLinkedin as Linkedin,
  FiChevronLeft as ChevronLeft,
  FiChevronRight as ChevronRight,
} from "react-icons/fi";
import { cn } from "../../lib/utils";

export function TestimonialCarousel({ testimonials = [], className }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!testimonials || testimonials.length === 0) return null;

  const handleNext = () =>
    setCurrentIndex((index) => (index + 1) % testimonials.length);
  const handlePrevious = () =>
    setCurrentIndex(
      (index) => (index - 1 + testimonials.length) % testimonials.length
    );

  const currentTestimonial = testimonials[currentIndex];

  const socialIcons = [
    { icon: Github, url: currentTestimonial.githubUrl, label: "GitHub" },
    { icon: Twitter, url: currentTestimonial.twitterUrl, label: "Twitter" },
    { icon: Youtube, url: currentTestimonial.youtubeUrl, label: "YouTube" },
    { icon: Linkedin, url: currentTestimonial.linkedinUrl, label: "LinkedIn" },
  ].filter(item => item.url); // Only render icons that have URLs provided

  return (
    <div className={cn("w-full max-w-5xl mx-auto px-4", className)}>
      {/* Desktop layout */}
      <div className='hidden md:flex relative items-center'>
        {/* Avatar */}
        <div className='w-[470px] h-[550px] rounded-[2.5rem] overflow-hidden bg-brand-sand flex-shrink-0 shadow-2xl relative border-4 border-white/50'>
          <AnimatePresence mode='wait'>
            <motion.div
              key={currentTestimonial.imageUrl}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className='absolute inset-0 w-full h-full'
            >
              <img
                src={currentTestimonial.imageUrl}
                alt={currentTestimonial.name}
                className='w-full h-full object-cover object-center'
                draggable={false}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Card */}
        <div className='bg-white/90 backdrop-blur-xl rounded-[2rem] shadow-[0_20px_50px_-15px_rgba(0,0,0,0.1)] border border-white p-10 ml-[-100px] z-10 max-w-xl flex-1'>
          <AnimatePresence mode='wait'>
            <motion.div
              key={currentTestimonial.name}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
            >
              <div className='mb-6'>
                <h2 className='text-3xl font-serif font-bold text-brand-darkSage mb-2'>
                  {currentTestimonial.name}
                </h2>
                <p className='text-xs font-semibold uppercase tracking-[0.2em] text-brand-gold'>
                  {currentTestimonial.title}
                </p>
              </div>

              <p className='text-brand-lightText text-base leading-relaxed mb-8 italic border-l-2 border-brand-gold/30 pl-5'>
                "{currentTestimonial.description}"
              </p>

              <div className='flex space-x-4'>
                {socialIcons.map(({ icon: IconComponent, url, label }) => (
                  <a
                    key={label}
                    href={url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='w-10 h-10 bg-brand-darkSage/5 text-brand-darkSage rounded-full flex items-center justify-center transition-all hover:bg-brand-gold hover:text-white hover:-translate-y-1'
                    aria-label={label}
                  >
                    <IconComponent className='w-4 h-4' />
                  </a>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Mobile layout */}
      <div className='md:hidden max-w-sm mx-auto text-center bg-transparent'>
        {/* Avatar */}
        <div className='w-full aspect-[4/5] bg-brand-sand rounded-[2rem] overflow-hidden mb-8 shadow-2xl relative border-4 border-white/50'>
          <AnimatePresence mode='wait'>
            <motion.div
              key={currentTestimonial.imageUrl}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className='absolute inset-0 w-full h-full'
            >
              <img
                src={currentTestimonial.imageUrl}
                alt={currentTestimonial.name}
                className='w-full h-full object-cover object-center'
                draggable={false}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Card content */}
        <div className='px-4 bg-white/80 backdrop-blur-md rounded-3xl p-6 shadow-xl border border-white'>
          <AnimatePresence mode='wait'>
            <motion.div
              key={currentTestimonial.name}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
            >
              <h2 className='text-2xl font-serif font-bold text-brand-darkSage mb-2'>
                {currentTestimonial.name}
              </h2>
              
              <p className='text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-gold mb-6'>
                {currentTestimonial.title}
              </p>
              
              <p className='text-brand-lightText text-sm leading-relaxed mb-6 italic text-left border-l-2 border-brand-gold/30 pl-4'>
                "{currentTestimonial.description}"
              </p>
              
              <div className='flex justify-center space-x-4'>
                {socialIcons.map(({ icon: IconComponent, url, label }) => (
                  <a
                    key={label}
                    href={url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='w-10 h-10 bg-brand-darkSage/5 text-brand-darkSage rounded-full flex items-center justify-center transition-all hover:bg-brand-gold hover:text-white'
                    aria-label={label}
                  >
                    <IconComponent className='w-4 h-4' />
                  </a>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Bottom navigation */}
      <div className='flex justify-center items-center gap-6 mt-12'>
        <button
          onClick={handlePrevious}
          aria-label='Previous'
          className='w-12 h-12 rounded-full bg-white border border-brand-darkSage/10 shadow-sm flex items-center justify-center hover:bg-brand-gold hover:text-white hover:border-brand-gold text-brand-darkSage transition-all'
        >
          <ChevronLeft className='w-6 h-6' />
        </button>

        <div className='flex gap-2 items-center'>
          {testimonials.map((_, testimonialIndex) => (
            <button
              key={testimonialIndex}
              onClick={() => setCurrentIndex(testimonialIndex)}
              className={cn(
                "h-2 rounded-full transition-all duration-300",
                testimonialIndex === currentIndex
                  ? "bg-brand-gold w-6"
                  : "bg-brand-darkSage/20 w-2 hover:bg-brand-darkSage/40"
              )}
              aria-label={`Go to ${testimonialIndex + 1}`}
            />
          ))}
        </div>

        <button
          onClick={handleNext}
          aria-label='Next'
          className='w-12 h-12 rounded-full bg-white border border-brand-darkSage/10 shadow-sm flex items-center justify-center hover:bg-brand-gold hover:text-white hover:border-brand-gold text-brand-darkSage transition-all'
        >
          <ChevronRight className='w-6 h-6' />
        </button>
      </div>
    </div>
  );
}